import { 
  ArrowRight,
  CheckCircle2,
  Shield,
  Users,
  Award,
  Target,
  Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const About = ({ onContactClick }) => {
  return (
    <main className="pt-16 lg:pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-white" data-testid="about-hero">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Über uns</span>
              <h1 className="heading-xl text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Ihr Partner für Energie-Infrastruktur im Gesundheitswesen
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Wir sind spezialisiert auf Batteriespeicherlösungen für Krankenhäuser und Kliniken. 
                Unser Fokus: Wirtschaftlichkeit, Zuverlässigkeit und Sicherheit auf höchstem Niveau.
              </p>
              <Button 
                onClick={onContactClick}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="about-cta-btn"
              >
                Kontakt aufnehmen
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_hospital-bess-losung/artifacts/nc1pnofa_Hospital_BESS8.png"
                  alt="Medizinisches Team vor Krankenhaus"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section-padding bg-white" data-testid="mission-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Unsere Mission
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Wir machen Krankenhäuser energieeffizienter und versorgungssicherer – 
              mit wirtschaftlich sinnvollen Lösungen, die sich selbst finanzieren.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Wirtschaftlichkeit
              </h3>
              <p className="text-slate-600">
                Jede Lösung muss sich rechnen. Wir optimieren für den besten ROI – transparent und nachvollziehbar.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Sicherheit
              </h3>
              <p className="text-slate-600">
                Im Krankenhaus zählt jede Sekunde. Unsere Systeme erfüllen höchste Sicherheitsstandards.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Partnerschaft
              </h3>
              <p className="text-slate-600">
                Wir denken langfristig. Von der ersten Analyse bis zum laufenden Betrieb – wir sind Ihr Partner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="section-padding bg-slate-50" data-testid="why-us">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#10B981] font-medium text-sm uppercase tracking-wide">Warum wir</span>
              <h2 className="heading-lg text-[#0F172A] mt-2 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Spezialisiert auf das Gesundheitswesen
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Anders als allgemeine Energiedienstleister verstehen wir die besonderen Anforderungen 
                von Krankenhäusern: kritische Versorgungssicherheit, komplexe Tarifstrukturen und 
                strenge regulatorische Rahmenbedingungen.
              </p>

              <div className="space-y-4">
                {[
                  'Langjährige Erfahrung im Healthcare-Sektor',
                  'Verständnis für Entscheidungsprozesse in Kliniken',
                  'Expertise bei Notstromversorgung und USV-Integration',
                  'Kenntnis relevanter Förderprogramme',
                  'Zertifizierte Partner für kritische Infrastruktur',
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card className="card-feature text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-[#10B981] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    10+
                  </div>
                  <p className="text-sm text-slate-600">Jahre Erfahrung</p>
                </CardContent>
              </Card>

              <Card className="card-feature text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-[#10B981] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    25+
                  </div>
                  <p className="text-sm text-slate-600">Projekte realisiert</p>
                </CardContent>
              </Card>

              <Card className="card-feature text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-[#10B981] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    15 MWh
                  </div>
                  <p className="text-sm text-slate-600">Installierte Kapazität</p>
                </CardContent>
              </Card>

              <Card className="card-feature text-center">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-[#10B981] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    98%
                  </div>
                  <p className="text-sm text-slate-600">Kundenzufriedenheit</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-white" data-testid="team-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Unser Team
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Ein interdisziplinäres Team aus Ingenieuren, Energiewirtschaftlern und 
              Projektmanagern mit Leidenschaft für nachhaltige Energielösungen.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { role: 'Projektleitung', desc: 'Erfahrene Projektmanager für reibungslose Umsetzung', icon: Users },
              { role: 'Technik', desc: 'Elektroingenieure und Speichersystem-Experten', icon: Shield },
              { role: 'Vertrieb', desc: 'Berater mit Healthcare-Hintergrund', icon: Target },
              { role: 'Service', desc: '24/7 Support und Wartungsteam', icon: Award },
            ].map((member, index) => (
              <Card key={index} className="card-feature text-center">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <member.icon className="w-7 h-7 text-[#0F172A]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {member.role}
                  </h3>
                  <p className="text-sm text-slate-600">{member.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-slate-50" data-testid="values-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Unsere Werte
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Transparenz', desc: 'Offene Kommunikation, nachvollziehbare Kalkulation, ehrliche Beratung.' },
              { title: 'Qualität', desc: 'Nur geprüfte Komponenten, zertifizierte Partner, höchste Standards.' },
              { title: 'Verlässlichkeit', desc: 'Was wir zusagen, halten wir. Termine, Budgets, Leistung.' },
              { title: 'Nachhaltigkeit', desc: 'Langfristig denken – für unsere Kunden und die Umwelt.' },
            ].map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border border-slate-100">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="text-sm text-slate-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-padding bg-white" data-testid="certifications">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Zertifizierungen & Mitgliedschaften
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-8 items-center">
            {[
              'TÜV-zertifiziert',
              'ISO 9001',
              'VDE-Mitglied',
              'BVES-Partner',
              'Made in Germany',
            ].map((cert, index) => (
              <div key={index} className="flex items-center gap-2 bg-slate-50 rounded-lg px-4 py-3 border border-slate-100">
                <Award className="w-5 h-5 text-[#10B981]" />
                <span className="text-sm font-medium text-slate-700">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0F172A] text-white" data-testid="about-final-cta">
        <div className="container-main text-center">
          <h2 className="heading-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Lassen Sie uns ins Gespräch kommen
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Wir freuen uns darauf, Sie und Ihr Krankenhaus kennenzulernen. 
            Vereinbaren Sie ein unverbindliches Erstgespräch.
          </p>
          <Button 
            onClick={onContactClick}
            className="bg-[#10B981] hover:bg-emerald-600 text-white px-8 py-4 rounded-md font-medium inline-flex items-center gap-2"
            data-testid="about-cta-final"
          >
            Kontakt aufnehmen
            <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </main>
  );
};
